import { useCallback } from "react";
import { PlaidConnection } from "@common/types/plaidConnection";
import { Task } from "@common/types/task";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useEnablePlaidStatementsMutation } from "@mutations/plaidConnection";
import { useTaskPlaidConnection } from "../shared/useTaskPlaidConnection";
import { getConnectionMetadata } from "../TaskBody.lib";

interface Props {
    selectedTask: Task;
}

const EnableStatementsButton = ({
    connection,
}: {
    connection: PlaidConnection;
}) => {
    const { mutate: enableStatements, isPending } =
        useEnablePlaidStatementsMutation(connection);
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const viewOnlyTooltip = useViewOnlyTooltipReason("");

    const handleEnableStatements = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        enableStatements();
    }, [refuseIfViewOnly, enableStatements]);

    return (
        <Tooltip content={viewOnlyTooltip}>
            <ButtonWithLoader
                loading={isPending}
                variant="primary"
                size="md"
                onClick={handleEnableStatements}
                disabled={isViewOnly}
            >
                Enable in Plaid
            </ButtonWithLoader>
        </Tooltip>
    );
};

const TaskPlaidStatementsRefreshAction = ({ selectedTask }: Props) => {
    const { connectionId, isIntegrationConnectionId } =
        getConnectionMetadata(selectedTask);

    const { plaidConnection, isLoading } = useTaskPlaidConnection(
        isIntegrationConnectionId ? null : connectionId,
    );
    const enableSyncTooltip = useViewOnlyTooltipReason(
        "Only connection owner can enable statement sync",
    );

    if (!connectionId || isIntegrationConnectionId || isLoading) {
        return null;
    }

    if (!plaidConnection) {
        return (
            <Tooltip content={enableSyncTooltip}>
                <Button disabled variant="primary" size="md">
                    Enable in Plaid
                </Button>
            </Tooltip>
        );
    }

    return <EnableStatementsButton connection={plaidConnection} />;
};

export const StatementsRefreshCommandIsland = ({ selectedTask }: Props) => (
    <CommandIsland alignEnd className="mx-3" sticky>
        <TaskPlaidStatementsRefreshAction selectedTask={selectedTask} />
    </CommandIsland>
);
